// Imports
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Tag, Tooltip, Space, Tabs } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import Loader from "../../../../core/common/loader";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { notify } from "../../../../services/toast.config";
import api from "../../../../services/api";

import TemplateDetailsModal, {
  TemplateData,
} from "./modals/TemplateDetailsModal";

import Datatable from "../../../../core/common/dataTable";
import AddTemplateModal, { TemplateRecord } from "./modals/AddTemplateModal";
import ConfirmationModal from "../../../ers/common/components/ConfirmationModal";

// Helpers
const getTemplateStatusColor = (status: string) => {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

// Component
const ConfigTemplatesPage: React.FC = () => {
  // Route & navigation
  const { configId } = useParams<{ configId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const companyId = state?.companyId;
  const companyType = state?.companyType;

  const title = state.title || "Templates";
  const breadcrumb = state.breadcrumb || "Templates";
  const backRoute = state.backRoute || all_routes.whatsappConfigurations;
  const dashboardRoute = state.dashboardRoute || all_routes.adminDashboard;
  const supportsTemplateSync = state.supportsTemplateSync || false;

  // State: loading & error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State: data
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>("all");

  // State: selection / modal
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateData | null>(
    null
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<TemplateData | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState<"add" | "edit">(
    "add"
  );
  const [editingTemplate, setEditingTemplate] = useState<TemplateRecord | null>(
    null
  );

  // State: sync button
  const [isSyncing, setIsSyncing] = useState(false);

  // Derived
  const filteredTemplates = useMemo(() => {
    if (!statusFilter || statusFilter === "all") return templates;
    return templates.filter(
      (t) => t?.status?.toLocaleLowerCase() === statusFilter
    );
  }, [templates, statusFilter]);

  const summaryStats = useMemo(
    () => ({
      total: templates.length,
      approved: templates.filter((t) => t?.status?.toLowerCase() === "approved")
        .length,
      rejected: templates.filter((t) => t?.status?.toLowerCase() === "rejected")
        .length,
      pending: templates.filter((t) => t?.status?.toLowerCase() === "pending")
        .length,
    }),
    [templates]
  );

  // Delete handler - open confirmation modal
  const handleDeleteTemplate = useCallback((record: TemplateData) => {
    setDeleteRecord(record);
    setIsDeleteModalOpen(true);
  }, []);

  // Confirm delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteRecord) return;

    try {
      setIsDeleteLoading(true);

      const signature = localStorage.getItem("signature");

      await api.post("whatsappService/delete-templates", {
        signature,
        config_id: Number(configId),
        company_id: Number(companyId),
        company_type: companyType,
        id: deleteRecord.id,
      });

      notify.success(`Template "${deleteRecord.name}" deleted successfully`);

      // Close modal and reload templates
      setIsDeleteModalOpen(false);
      setDeleteRecord(null);
      await loadTemplates();
    } catch (e: any) {
      const errMsg =
        e?.response?.data?.message?.[0]?.msg ||
        e?.data?.message?.[0]?.msg ||
        e?.message ||
        "Failed to delete template";
      notify.error(errMsg);
    } finally {
      setIsDeleteLoading(false);
    }
  }, [deleteRecord, configId, companyId, companyType]);

  // Columns
  const columns: ColumnsType<TemplateData> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <span className="fw-semibold">{text}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => <span className="text-muted">{text || "-"}</span>,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 100,
      render: (text) => <span className="text-muted">{text || "-"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getTemplateStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          {moment(date).format("MMM DD, YYYY HH:mm")}
        </Tooltip>
      ),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 180,
      sorter: (a, b) =>
        new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          {moment(date).format("MMM DD, YYYY HH:mm")}
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isDeleting = isDeleteLoading && deleteRecord?.id === record.id;

        return (
          <Space size="small">
            <button
              title="Show Message Logs"
              className="btn btn-md btn-success"
              onClick={() =>
                navigate(
                  all_routes.whatsappMessageLogs
                    .replace(":configId", configId!)
                    .replace(":templateId", String(record.id)),
                  {
                    state: {
                      companyId,
                      companyType,
                      templateName: record.name,
                      backRoute: location.pathname,
                      dashboardRoute,
                      title,
                      breadcrumb,
                      supportsTemplateSync,
                      configBackRoute: backRoute,
                    },
                  }
                )
              }
              disabled={isDeleting}>
              <i className="ti ti-message-circle me-1" />
              Logs
            </button>
            <button
              title="View Details"
              className="btn btn-md btn-info"
              onClick={() => {
                setSelectedTemplate(record);
                setIsDetailsModalOpen(true);
              }}
              disabled={isDeleting}>
              <i className="ti ti-eye" />
            </button>

            {record?.is_manual_entry && (
              <>
                <button
                  title="Edit Template"
                  className="btn btn-md btn-secondary"
                  onClick={() => handleEditTemplate(record)}
                  disabled={isDeleting}>
                  <i className="ti ti-edit" />
                </button>

                <button
                  title="Delete Template"
                  className="btn btn-md btn-danger"
                  onClick={() => handleDeleteTemplate(record)}
                  disabled={isDeleting}>
                  {isDeleting ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-trash" />
                  )}
                </button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  // Data loader
  const loadTemplates = useCallback(async () => {
    if (!configId) {
      setError("Invalid configuration id");
      return;
    }
    if (!companyId || !companyType) {
      setError("Missing company context for this configuration");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const signature = localStorage.getItem("signature");

      const response = await api.post("whatsappService/get-templates", {
        signature,
        config_id: Number(configId),
        company_id: Number(companyId),
        company_type: companyType,
      });

      let data: TemplateData[] = [];
      if (Array.isArray(response.data?.data)) {
        data = response.data.data;
      } else if (
        response.data?.message &&
        Array.isArray(response.data.message)
      ) {
        data = response.data.message[0]?.data || [];
      }

      setTemplates(data);
    } catch (e: any) {
      const errMsg =
        e?.response?.data?.message?.[0]?.msg ||
        e?.data?.message?.[0]?.msg ||
        e?.message ||
        "Failed to load templates";
      setError(errMsg);
      notify.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [configId, companyId, companyType]);

  // Sync templates
  const handleSyncTemplates = useCallback(async () => {
    if (!configId || !companyId || !companyType) {
      notify.error("Missing configuration or company context for sync");
      return;
    }

    if (!supportsTemplateSync) {
      notify.error("This provider requires manual template entry");
      return;
    }

    try {
      setIsSyncing(true);

      const signature = localStorage.getItem("signature");

      const response = await api.post("whatsappService/sync-templates", {
        signature,
        company_id: Number(companyId),
        company_type: companyType,
        config_id: Number(configId),
      });

      const message =
        response.data?.message?.[0]?.msg || "Templates synced successfully";

      notify.success(message);

      await loadTemplates();
    } catch (e: any) {
      const data = e?.response?.data?.message?.[0] || e?.data?.message?.[0];
      const errMsg = data?.msg || "Failed to sync templates";

      notify.error(errMsg);

      if (
        data?.supports_template_sync != null &&
        data?.supports_template_sync != undefined &&
        data?.supports_template_sync == false
      ) {
        setTimeout(() => {
          navigate(backRoute);
        }, 1000);
      }
    } finally {
      setIsSyncing(false);
    }
  }, [
    configId,
    companyId,
    companyType,
    supportsTemplateSync,
    loadTemplates,
    navigate,
    backRoute,
  ]);

  const handleEditTemplate = (record: TemplateData) => {
    // Convert TemplateData to TemplateRecord format
    const templateRecord: any = {
      id: record.id,
      name: record.name,
      language: record.language,
      category: record.category,
      campaign_name: record.campaign_name,
      structure: record.structure,
      status: record.status,
      is_manual_entry: record.is_manual_entry,
    };

    setEditingTemplate(templateRecord);
    setTemplateModalMode("edit");
    setIsAddTemplateModalOpen(true);
  };

  // Add handler for opening add modal
  const handleAddNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateModalMode("add");
    setIsAddTemplateModalOpen(true);
  };

  // Update the handleAddTemplate to handle both add and edit
  const handleTemplateSubmit = async (templateData: any) => {
    const endpoint =
      templateModalMode === "edit"
        ? "whatsappService/update-templates"
        : "whatsappService/add-templates";

    await api.post(endpoint, templateData);

    const successMsg =
      templateModalMode === "edit"
        ? "Template updated successfully"
        : "Template created successfully";

    notify.success(successMsg);
    setIsAddTemplateModalOpen(false);
    await loadTemplates();
  };

  // Effects
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Error view
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Templates</h4>
            <p>{error}</p>
            <hr />
            <div className="d-flex gap-2">
              <button
                onClick={() => navigate(backRoute)}
                className="btn btn-primary">
                <i className="ti ti-arrow-left me-2" />
                Back to Configurations
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary">
                <i className="ti ti-reload me-2" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tabs config (mirrors provider tabs style)
  const statusTabs = [
    {
      key: "all",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-list-check me-2" />
          <span className="fw-medium">All</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.total}
          </span>
        </span>
      ),
    },
    {
      key: "approved",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-checks me-2 text-success" />
          <span className="fw-medium">Approved</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.approved}
          </span>
        </span>
      ),
    },
    {
      key: "pending",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-clock me-2 text-warning" />
          <span className="fw-medium">Pending</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.pending}
          </span>
        </span>
      ),
    },
    {
      key: "rejected",
      label: (
        <span className="d-flex align-items-center">
          <i className="ti ti-x me-2 text-danger" />
          <span className="fw-medium">Rejected</span>
          <span
            className="badge badge-sm bg-light text-dark ms-2"
            style={{ fontSize: 10 }}>
            {summaryStats.rejected}
          </span>
        </span>
      ),
    },
  ];

  // Main render
  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{title}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={dashboardRoute}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={backRoute}>WhatsApp Config</Link>
                  </li>
                  <li className="breadcrumb-item active">{breadcrumb}</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <button
                  className="btn btn-outline-secondary d-inline-flex align-items-center"
                  onClick={() => navigate(backRoute)}>
                  <i className="ti ti-arrow-left me-2" />
                  Back to Config
                </button>
              </div>

              <div className="d-flex align-items-center gap-2 mb-2">
                {supportsTemplateSync ? (
                  <button
                    className="btn btn-success d-inline-flex align-items-center"
                    onClick={handleSyncTemplates}
                    disabled={isSyncing}>
                    {isSyncing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-refresh me-2" />
                        Sync Templates
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    className="btn btn-primary d-inline-flex align-items-center"
                    onClick={handleAddNewTemplate}>
                    <i className="ti ti-plus me-2" />
                    Add Template
                  </button>
                )}
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Card with tabs */}
          <div className="card">
            <div className="card-header border-0 pb-0">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h4 className="mb-1 d-flex align-items-center">
                    <i className="ti ti-template text-primary fs-24 me-2" />
                    Provider Templates
                  </h4>
                  <p className="text-muted mb-0 small">
                    Templates synced or associated with this WhatsApp
                    configuration
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge badge-lg bg-gradient-primary text-white">
                    <i className="ti ti-database me-1" />
                    {summaryStats.total} Total
                  </span>
                </div>
              </div>

              {/* Status Tabs (like provider tabs) */}
              <Tabs
                activeKey={statusFilter || "all"}
                onChange={(key) => setStatusFilter(key === "all" ? "all" : key)}
                type="line"
                size="small"
                className="modern-tabs"
                items={statusTabs}
              />
            </div>

            {/* Table / empty state */}
            <div className="card-body p-0 bg-white" style={{ minHeight: 400 }}>
              {filteredTemplates.length > 0 ? (
                <Datatable columns={columns} dataSource={filteredTemplates} />
              ) : (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 320 }}>
                  <div className="text-center">
                    <div className="mb-3">
                      <i
                        className="ti ti-template-off text-danger"
                        style={{ fontSize: "4rem" }}
                      />
                    </div>
                    <h5 className="mt-3 mb-2 fw-semibold">
                      No Templates Found
                    </h5>
                    <p className="text-muted mb-0">
                      No templates are currently available for this
                      configuration.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </div>
      </div>

      {/* Template details modal */}
      <TemplateDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        template={selectedTemplate}
      />

      {/* Add/Edit Template modal */}
      <AddTemplateModal
        isOpen={isAddTemplateModalOpen}
        mode={templateModalMode}
        templateRecord={editingTemplate}
        onClose={() => {
          setIsAddTemplateModalOpen(false);
          setEditingTemplate(null);
        }}
        onSubmit={handleTemplateSubmit}
        configId={configId!}
        companyId={Number(companyId)}
        companyType={companyType}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleteLoading && setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        message={`Are you sure you want to delete the template "${deleteRecord?.name}"? This action cannot be undone and may affect existing workflows using this template.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleteLoading}
        showCloseButton={false}
      />
    </>
  );
};

export default ConfigTemplatesPage;
